/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./CreditPlanDetailScreen.css";
import backIcon from "../../../assets/images/back-icon.svg";
import orangeStarIcon from "../../../assets/images/membership-orange-star.svg";
import { useNavigate } from "react-router-dom";
import {
  CALENDAR_RESERVATION_TABS,
  PAYMENT_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import useWSMApi from "services/api/useWSMApi";
import { useMutation } from "@tanstack/react-query";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { CircularProgress } from "@mui/material";
import { MESSAGE } from "utils/strings";
import ROUTE_PATHS from "utils/routePaths";
import { useStorage } from "services/StorageProvider";

const CreditPlanDetailScreen: FC = () => {
  const navigate = useNavigate();
  const { userProfileData } = useStorage();
  const [selectedCreditPlanData, setSelectedCreditPlanData] =
    useState<any>(null);
  const isBundle = selectedCreditPlanData?.credit_service_types?.length > 1;
  const firstServiceType = selectedCreditPlanData?.credit_service_types?.[0];
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string>("");
  const { createPayment, verifyPayment } = useWSMApi();
  const serviceName =
    CALENDAR_RESERVATION_TABS.find((type) => type.value === firstServiceType)
      ?.name || "";

  const purchaseCreditPlanMutation = useMutation({
    mutationFn: (data: any) => createPayment(data),
    onSuccess: (response, obj) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: obj.product.total_price,
        currency: obj.currency,
        name: "The Foundry", //Name Shown in Razorpay Form
        description: obj.product.description,
        order_id: response.id, // This is the order_id created in the backend
        prefill: {
          name: obj.customer.name,
          email: obj.customer.email,
          contact: obj.customer.contact,
        },
        theme: {
          color: "#1C1C1E",
        },
        handler: async function (paymentResponse: any) {
          setPaymentId(paymentResponse.razorpay_payment_id);
          verifyPaymentMutation.mutate({
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            if (!paymentStatus) {
              setPaymentStatus(PAYMENT_STATUS.CANCELLED);
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    },
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: (data: any) => verifyPayment(data),
    onSuccess: (response) => {
      setPaymentStatus(response.status);
    },
    onError: () => {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
    },
  });

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      const paymentData = {
        title: isBundle ? "Bundle Pack" : `Single Credit Pack`,
        type: PURCHASE_TYPE.CREDIT,
        serviceType: selectedCreditPlanData?.credit_service_types[0],
      };
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      navigate(ROUTE_PATHS.WSM_ALL_SET, { replace: true });
    }
  }, [paymentStatus, paymentId, navigate]);

  useEffect(() => {
    const storedData = localStorage.getItem("selectedCreditPlanData");
    if (storedData) {
      setSelectedCreditPlanData(JSON.parse(storedData));
    }
  }, []);

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const handlePurchaseCreditPlanClick = () => {
    const planPrice =
      Number(selectedCreditPlanData?.credit_price) + getGSTPrice();
    const amountInSubunits = Math.round(planPrice * 100);
    const passedObj = {
      amount: planPrice,
      currency: "INR",
      customer: {
        name: `${userProfileData?.first_name} ${userProfileData?.last_name}`,
        email: userProfileData?.email,
        contact: userProfileData?.mobile_number,
      },
      product: {
        total_price: amountInSubunits, // Amount in paise
        credit_plan_id: selectedCreditPlanData?.id,
        description: `${isBundle ? "Bundle Pack" : `Single ${serviceName}`}`,
        price: selectedCreditPlanData?.credit_price,
        with_gst_price: getGSTPrice(),
        credit: selectedCreditPlanData?.total_credits,
        validity: selectedCreditPlanData?.credit_validity,
      },
    };
    setPaymentStatus(null);
    purchaseCreditPlanMutation.mutate(passedObj);
  };

  const getGSTPrice = () => {
    const gstPrice =
      (Number(selectedCreditPlanData?.credit_price) *
        selectedCreditPlanData?.credit_gst) /
      100;
    return Math.floor(gstPrice);
  };

  const getServiceTypeParam = (serviceType: any): string => {
    switch (serviceType) {
      case "DF_WORKSPACE":
        return "dfw_credits";

      case "MB_STUDIO":
        return "mbs_credits";

      case "EVENTS":
        return "event_credits";

      case "WORKSHOP":
        return "workshop_credits";

      default:
        return "";
    }
  };

  return (
    <div className="wms-mobile-container">
      {purchaseCreditPlanMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {purchaseCreditPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {paymentStatus === PAYMENT_STATUS.FAILED ? (
        <SnackbarAlert type={"error"}>{MESSAGE.PAYMENT_FAILED}</SnackbarAlert>
      ) : (
        ""
      )}
      <div className="credit-plan-detail-screen-container">
        <div className="credit-plan-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">
            {isBundle ? "Bundle Pack" : `Single Credit Pack`}
          </div>
        </div>
        <div className="credit-plan-detail-content-container">
          <div className="header-container">
            <span>{`${selectedCreditPlanData?.total_credits} ${
              selectedCreditPlanData?.total_credits > 1 ? "Credits" : "Credit"
            }`}</span>
            <div className="price-container">{`₹ ${selectedCreditPlanData?.credit_price?.toLocaleString(
              "en-US",
            )}`}</div>
          </div>
          <div className="divider"></div>
          {selectedCreditPlanData?.credit_service_types?.map(
            (serviceType: any, index: number) => (
              <div
                key={index}
                className="credit-plan-detail-content-text-container"
              >
                <img src={orangeStarIcon} alt="star-icon" />
                <div className="detail-text">{`${
                  CALENDAR_RESERVATION_TABS.find(
                    (type) => type.value === serviceType,
                  )?.name
                } (${
                  selectedCreditPlanData?.[getServiceTypeParam(serviceType)]
                } Credits)`}</div>
              </div>
            ),
          )}
        </div>
        <div className="credit-plan-detail-content-container">
          <span>Validity</span>
          <div className="divider"></div>
          <div className="credit-plan-detail-content-text-container">
            <img src={orangeStarIcon} alt="star-icon" />
            <div className="detail-text">
              {userProfileData?.credit?.membership
                ? "Till Active Membership"
                : `${selectedCreditPlanData?.credit_validity} Months`}
            </div>
          </div>
        </div>
        <div className="credit-plan-detail-content-container">
          <span>Details</span>
          <div className="divider"></div>
          <div className="credit-plan-detail-content-text-container">
            <div className="detail-text">
              {selectedCreditPlanData?.discription}
            </div>
          </div>
        </div>
      </div>
      <div
        className="credit-plan-detail-purchase-btn"
        onClick={handlePurchaseCreditPlanClick}
      >
        {purchaseCreditPlanMutation?.isPending ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          "Purchase"
        )}
      </div>
    </div>
  );
};

export default CreditPlanDetailScreen;
